import { WithSkeleton } from '@capsicum/common';
import { RoundedCardContainer, SkeletonItem, Tooltip, type TooltipContentPosition } from '@capsicum/common-web';
import styled from 'styled-components';

export type SummarySectionItemProps = {
	isFlexDirectionRow?: boolean;
	isLoadingValue?: boolean;
	label?: string;
	testID?: string;
	tooltipPosition?: TooltipContentPosition;
	tooltipText?: string;
	tooltipTitle?: string;
	value?: string;
	withTooltip?: boolean;
};

const SummarySectionItemComponent = ({
	isFlexDirectionRow = false,
	isLoadingValue = false,
	label,
	testID,
	tooltipPosition = 'bottom',
	tooltipText,
	tooltipTitle,
	value,
	withTooltip
}: SummarySectionItemProps) => {
	return (
		<S.Wrapper $isFlexDirectionRow={isFlexDirectionRow}>
			<S.Label data-testid={testID}>
				{label}
				{withTooltip && tooltipText && (
					<Tooltip contentPosition={tooltipPosition}>
						<Tooltip.Trigger testID={testID + 'Tooltip'} />
						<Tooltip.Content>
							<Tooltip.Title>{tooltipTitle}</Tooltip.Title>
							<Tooltip.Description>{tooltipText}</Tooltip.Description>
						</Tooltip.Content>
					</Tooltip>
				)}
			</S.Label>
			<S.Value>{isLoadingValue ? <SkeletonItem height={15} width={120} /> : value}</S.Value>
		</S.Wrapper>
	);
};

const SummarySectionItemSkeleton = () => (
	<S.Wrapper>
		<SkeletonItem height={15} width={120} />
		<SkeletonItem height={15} width={120} />
	</S.Wrapper>
);

namespace S {
	export const Wrapper = styled(RoundedCardContainer)<{
		$isFlexDirectionRow?: boolean;
	}>`
		display: flex;
		flex-direction: ${({ $isFlexDirectionRow }) => ($isFlexDirectionRow ? 'row' : 'column')};
		padding: ${({ theme }) => theme.spacings.small};
		row-gap: ${({ theme }) => theme.spacings.xxxsmall};
		flex-grow: 1;
		justify-content: space-between;
	`;

	export const Label = styled.span`
		display: flex;
		align-items: center;
		column-gap: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.text3}
		text-align: left;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Value = styled.span`
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
}

export const SummarySectionItem = WithSkeleton(SummarySectionItemComponent, SummarySectionItemSkeleton);
